import {AxiosError, AxiosResponse, HttpStatusCode, isAxiosError} from 'axios';

export const NETWORK_ERROR_MESSAGE =
    'It looks like there was a problem with your internet connection. Please check your connection and refresh the page to try again.';

/**
 * Checks if the axios error is a network error
 */
export function isNetworkError(e: unknown): e is AxiosError {
    return (
        isAxiosError(e) &&
        (e.code?.toUpperCase() === 'ERR_NETWORK' || e.code?.toUpperCase() === 'ECONNABORTED')
    );
}

/**
 * Checks if the axios error is a gateway error
 */
export function isGatewayError(e: unknown): e is AxiosError {
    return (
        isAxiosError(e) &&
        (e.response?.status === HttpStatusCode.BadGateway ||
            e.response?.status === HttpStatusCode.GatewayTimeout)
    );
}

/**
 * Returns the default error message for a network and non-network error
 */
export function getDefaultErrorMessage(error: unknown) {
    return isNetworkError(error)
        ? NETWORK_ERROR_MESSAGE
        : 'Something went wrong. Please try again later.';
}

/**
 * Retries axios request if it's a network error or gateway error
 */
export async function retryAxiosError<T extends Promise<AxiosResponse>>(
    initiateRequest: (attempt: number) => T,
    params = {
        attempt: 1,
        delay: 500,
        maxAttempts: 5,
    },
): Promise<Awaited<T>> {
    try {
        return await initiateRequest(params.attempt);
    } catch (e) {
        if ((isNetworkError(e) || isGatewayError(e)) && params.attempt < params.maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, params.delay));
            return await retryAxiosError(initiateRequest, {
                ...params,
                attempt: params.attempt + 1,
            });
        }
        throw e;
    }
}
